/**
 * Support thread — one conversation, end to end.
 *
 * Shows the full message log (customer, operator, and system messages), renders image
 * attachments inline and offers non-image files for download (both fetched over the
 * auth-scoped `/admin/support/media/:id` route, never a bare `<img src>`), and gives the
 * operator a composer to reply with text and/or a file. The header carries assign-to-me,
 * a priority control (via the assign route) and a status control that rides along with the
 * reply (per the contract, a status move accompanies an operator message). While the
 * thread is open it polls for new messages so an incoming customer reply appears live.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { messageOf } from '../api/errors.js';
import type { MediaObject } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import type {
  AdminThreadDetail,
  Attachment,
  Message,
  SupportPriority,
  SupportStatus,
} from '../contracts/types.js';
import { SUPPORT_PRIORITIES, SUPPORT_STATUSES } from '../contracts/vocab.js';
import { Button, Card, ErrorBox, LoadingState, Select, Textarea } from '../components/ui.js';
import { ContactPhone } from '../components/ContactPhone.js';
import { SupportPriorityBadge, SupportStatusBadge } from '../components/badges.js';
import {
  IconArrowLeft,
  IconDownload,
  IconPaperclip,
  IconSend,
  IconX,
} from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { formatDateTime, humanize, relativeTime } from '../lib/format.js';

const POLL_MS = 5000;

function fileToInput(file: File): Promise<{ data: string; mime: string; name: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve({
        data: comma >= 0 ? result.slice(comma + 1) : result,
        mime: file.type || 'application/octet-stream',
        name: file.name,
      });
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

export function SupportThreadPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { api, session } = useAuth();
  const toast = useToast();
  const meId = session?.me.id ?? null;

  const [detail, setDetail] = useState<AdminThreadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Composer
  const [body, setBody] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<SupportStatus>('open');
  const [sending, setSending] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const scrollAnchor = useRef<HTMLDivElement | null>(null);

  const load = useCallback(
    async (silent: boolean) => {
      if (!silent) setLoading(true);
      try {
        const next = await api.getThread(id);
        setDetail(next);
        setError(null);
        setStatus((prev) => (silent ? prev : next.thread.status));
      } catch (err) {
        if (!silent) setError(messageOf(err));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [api, id],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  // Poll for new messages while the thread is open.
  useEffect(() => {
    const timer = window.setInterval(() => void load(true), POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const messageCount = detail?.messages.length ?? 0;
  useEffect(() => {
    scrollAnchor.current?.scrollIntoView({ block: 'end' });
  }, [messageCount]);

  const thread = detail?.thread ?? null;

  async function send() {
    const text = body.trim();
    if (text === '' && file === null) return;
    setSending(true);
    try {
      const attachment = file !== null ? await fileToInput(file) : undefined;
      const req = {
        ...(text !== '' ? { body: text } : {}),
        ...(attachment !== undefined ? { attachment } : {}),
        ...(thread !== null && status !== thread.status ? { status } : {}),
      };
      const next = await api.reply(id, req);
      setDetail(next);
      setStatus(next.thread.status);
      setBody('');
      setFile(null);
      if (fileInput.current !== null) fileInput.current.value = '';
      toast.success('Reply sent');
    } catch (err) {
      toast.error('Could not send', messageOf(err));
    } finally {
      setSending(false);
    }
  }

  async function assignTo(assignedAdminId: string | null) {
    if (thread === null) return;
    try {
      const row = await api.assign(id, { assignedAdminId, priority: thread.priority });
      setDetail((prev) => (prev === null ? prev : { ...prev, thread: row }));
      toast.success(assignedAdminId === null ? 'Unassigned' : 'Assigned to you');
    } catch (err) {
      toast.error('Could not assign', messageOf(err));
    }
  }

  async function changePriority(priority: SupportPriority) {
    if (thread === null) return;
    try {
      const row = await api.assign(id, { assignedAdminId: thread.assignedAdminId, priority });
      setDetail((prev) => (prev === null ? prev : { ...prev, thread: row }));
      toast.success('Priority updated');
    } catch (err) {
      toast.error('Could not update priority', messageOf(err));
    }
  }

  const assignedToMe = thread !== null && meId !== null && thread.assignedAdminId === meId;
  const isClosed = thread?.status === 'closed';

  return (
    <>
      <div className="page-head">
        <Link to="/support" className="backlink">
          <IconArrowLeft size={16} /> Back to inbox
        </Link>
      </div>

      {loading ? (
        <LoadingState label="Loading conversation…" />
      ) : error !== null ? (
        <ErrorBox message={error} />
      ) : thread === null ? (
        <ErrorBox message="This conversation is no longer available." />
      ) : (
        <>
          <Card>
            <div className="thread-head">
              <div className="thread-head__main">
                <h2 className="thread-head__subject">{thread.subject}</h2>
                <div className="thread-head__meta">
                  <span style={{ fontWeight: 600 }}>{thread.displayName ?? 'Customer'}</span>
                  {/*
                    The whole number, and copyable here where the header has room for a button.

                    This is the screen an agent is on when they decide to stop typing and ring
                    the customer, so it is the one place in support where a mask costs the most:
                    a number read down a phone line that does not connect, with nothing on the
                    screen to say why. `ContactPhone` shows the full value where the API sent
                    one and labels the mask where it did not.
                  */}
                  <ContactPhone row={thread} />
                  <span className="faint">· {humanize(thread.type)}</span>
                  <span className="faint">· opened {relativeTime(thread.createdAt)}</span>
                </div>
              </div>
              <div className="thread-head__badges">
                <SupportStatusBadge status={thread.status} />
                <SupportPriorityBadge priority={thread.priority} />
              </div>
            </div>

            <div className="thread-controls">
              <label className="thread-controls__field">
                <span className="field__label">Priority</span>
                <Select
                  value={thread.priority}
                  onChange={(e) => void changePriority(e.target.value as SupportPriority)}
                >
                  {SUPPORT_PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {humanize(p)}
                    </option>
                  ))}
                </Select>
              </label>
              <div className="thread-controls__spacer" />
              {assignedToMe ? (
                <Button variant="ghost" onClick={() => void assignTo(null)}>
                  Unassign
                </Button>
              ) : (
                <Button variant="primary" onClick={() => void assignTo(meId)} disabled={meId === null}>
                  Assign to me
                </Button>
              )}
            </div>
          </Card>

          <div style={{ height: 16 }} />

          <Card flush title="Conversation">
            <div className="chat">
              {detail!.messages.map((m) => (
                <MessageBubble key={m.id} message={m} />
              ))}
              <div ref={scrollAnchor} />
            </div>
          </Card>

          <div style={{ height: 16 }} />

          <Card title="Reply">
            {isClosed ? (
              <p className="muted">This conversation is closed. Reopen it to send a reply.</p>
            ) : (
              <>
                <Textarea
                  value={body}
                  rows={3}
                  maxLength={4000}
                  placeholder="Write a reply to the customer…"
                  onChange={(e) => setBody(e.target.value)}
                />

                {file !== null && (
                  <div className="attach-chip" style={{ marginTop: 10 }}>
                    <IconPaperclip size={14} />
                    <span className="attach-chip__name">{file.name}</span>
                    <button
                      className="attach-chip__x"
                      aria-label="Remove attachment"
                      onClick={() => {
                        setFile(null);
                        if (fileInput.current !== null) fileInput.current.value = '';
                      }}
                    >
                      <IconX size={13} />
                    </button>
                  </div>
                )}

                <div className="composer-bar">
                  <input
                    ref={fileInput}
                    type="file"
                    hidden
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                  <Button variant="ghost" size="sm" onClick={() => fileInput.current?.click()}>
                    <IconPaperclip size={15} /> Attach
                  </Button>

                  <label className="composer-bar__status">
                    <span className="field__label">Set status</span>
                    <Select value={status} onChange={(e) => setStatus(e.target.value as SupportStatus)}>
                      {SUPPORT_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {humanize(s)}
                        </option>
                      ))}
                    </Select>
                  </label>

                  <div className="composer-bar__spacer" />
                  <Button
                    variant="accent"
                    loading={sending}
                    disabled={body.trim() === '' && file === null}
                    onClick={() => void send()}
                  >
                    <IconSend size={15} /> Send reply
                  </Button>
                </div>
              </>
            )}
          </Card>
        </>
      )}
    </>
  );
}

function MessageBubble({ message }: { message: Message }): JSX.Element {
  if (message.senderType === 'system') {
    return (
      <div className="chat-system">
        <span>{message.body}</span>
        <span className="chat-system__time">{formatDateTime(message.createdAt)}</span>
      </div>
    );
  }
  const side = message.mine ? 'out' : 'in';
  return (
    <div className={`chat-row chat-row--${side}`}>
      <div className={`bubble bubble--${side}`}>
        <div className="bubble__who">
          {message.mine ? 'You (operator)' : 'Customer'}
          <span className="bubble__time">{relativeTime(message.createdAt)}</span>
        </div>
        {message.body !== null && message.body !== '' && <div className="bubble__body">{message.body}</div>}
        {message.attachments.map((att) => (
          <MediaAttachment key={att.id} att={att} outgoing={message.mine} />
        ))}
      </div>
    </div>
  );
}

/**
 * Renders one attachment. Images load eagerly and render inline; other files show a
 * download chip that fetches the bytes on click. Both go through the bearer-scoped media
 * route via `getMediaObject`, which reads the raw bytes as a `Blob` (never `JSON.parse`d)
 * and hands back an object URL — so an image renders and a file downloads without smuggling
 * a token into a URL. The object URL is revoked on unmount to free the blob.
 */
function MediaAttachment({ att, outgoing }: { att: Attachment; outgoing: boolean }): JSX.Element {
  const { api } = useAuth();
  const isImage = att.mime.startsWith('image/');
  const [media, setMedia] = useState<MediaObject | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef<MediaObject | null>(null);

  const remember = useCallback((m: MediaObject) => {
    // Free any previously held URL before replacing it.
    mediaRef.current?.revoke();
    mediaRef.current = m;
    setMedia(m);
  }, []);

  // Free the object URL when this attachment unmounts.
  useEffect(() => {
    return () => {
      mediaRef.current?.revoke();
      mediaRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isImage) return;
    let live = true;
    api
      .getMediaObject(att.id)
      .then((m) => {
        if (live) remember(m);
        else m.revoke();
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [api, att.id, isImage, remember]);

  async function download() {
    setBusy(true);
    try {
      const m = mediaRef.current ?? (await api.getMediaObject(att.id));
      if (mediaRef.current === null) remember(m);
      const a = document.createElement('a');
      a.href = m.url;
      a.download = m.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  if (isImage) {
    if (failed) return <div className="attach-file attach-file--err">Image unavailable</div>;
    if (media === null) return <div className="attach-image attach-image--loading" aria-busy="true" />;
    return (
      <a className="attach-image" href={media.url} download={media.name} title="Open / download image">
        <img src={media.url} alt={att.name} />
      </a>
    );
  }

  return (
    <button
      className={`attach-file${outgoing ? ' attach-file--out' : ''}`}
      onClick={() => void download()}
      disabled={busy}
    >
      <IconDownload size={15} />
      <span className="attach-file__name">{att.name}</span>
      <span className="attach-file__size">{failed ? 'unavailable' : formatBytes(att.size)}</span>
    </button>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
